import {
    harnessLabel,
    RawConfiguredMcpServer,
    RawMcpServiceCallSummary,
    SaasAiAgentDto,
    SaasAiAgentKind,
} from '@ampsec/platform-client';
import {
    AgentRecord,
    ConfiguredMcpServerRecord,
    HarnessInstallRecord,
    McpToolCallRecord,
} from 'Core-utils/staticData/staticAgents';

const vendorFromHarness = (harness?: string): string => {
    const normalized = harness?.toLowerCase() ?? '';
    if (normalized.includes('cowork') || normalized.includes('claude')) {
        return 'Anthropic';
    }
    if (normalized.includes('gpt') || normalized.includes('codex')) {
        return 'OpenAI';
    }
    return (harness ?? 'Unknown') as string;
};

const formatServiceLabel = (serviceName: string): string => {
    if (!serviceName) {
        return serviceName;
    }
    return serviceName.charAt(0).toUpperCase() + serviceName.slice(1);
};

export const mcpToolCallsFromStatistics = (
    mcpServiceCalls: RawMcpServiceCallSummary[] | undefined,
): McpToolCallRecord[] => {
    if (!mcpServiceCalls?.length) {
        return [];
    }
    return mcpServiceCalls.map((entry) => ({
        service: formatServiceLabel(entry.service_name),
        count: entry.total_tool_calls,
    }));
};

const configuredMcpServerRecords = (servers: RawConfiguredMcpServer[] | undefined): ConfiguredMcpServerRecord[] =>
    (servers ?? []).map((server) => ({
        name: server.name,
        scope: server.scope ?? 'global',
        transport: server.transport ?? '-',
        target: server.package ?? server.urlHost ?? '-',
        envKeys: server.envKeys ?? [],
        headerKeys: server.headerKeys ?? [],
        deviceSn: server.sn,
        running: Boolean(server.running),
    }));

export const mapSaasAiAgentToAgentRecord = (dto: SaasAiAgentDto): AgentRecord => {
    // Prefer rollup statistics when available, fallback to session statistics
    const rollup = dto.meta?._rollup;
    const stats = dto.meta?._statistics;
    const session = dto.meta?._session;

    // Extract statistics from rollup or session stats
    const autonomyScore = rollup?.latest_autonomy_score ?? stats?.autonomy_score ?? undefined;
    const mcpServiceCalls = stats?.mcp_service_calls; // Session stats still holds detailed calls
    const sessionCount = rollup?.session_count ?? 1;

    const totalToolCalls = stats?.tool_calls ?? 0;
    const userTurns = stats?.human_turns ?? 0;
    const assistantTurns = stats?.assistant_turns ?? 0;
    const maxChain = stats?.max_chain ?? 0;
    const attestation = dto.userAttestation;
    const certifiedByOwner = Boolean(attestation?.user_attestation_date);
    const models = session?.models ?? [];
    const harnessType = session?.harness;
    // Endpoint harness agents carry their own label; vendor session harnesses
    // (`claude_code`, `cowork`) get the shared label map.
    const harness = dto.meta?._harness?.label ?? (harnessType ? harnessLabel(harnessType, harnessType) : 'Claude');
    const configuredMcpServers = configuredMcpServerRecords(dto.meta?._mcp?.configured);
    const installs: HarnessInstallRecord[] | undefined = dto.meta?._harness?.installs.map((install) => ({
        deviceSn: install.sn,
        rootPath: install.rootPath,
        version: install.version ?? '-',
        lastSeenAt: install.lastSeenAt ?? '',
    }));
    const observedMcpServers = mcpServiceCalls?.map((entry) => formatServiceLabel(entry.service_name)) ?? [];
    const configuredNames = configuredMcpServers.map((server) => formatServiceLabel(server.name));

    // Set routineId only for ROUTINE kind, using extKey as the routine identifier
    const routineId = dto.kind === SaasAiAgentKind.ROUTINE ? dto.extKey : undefined;

    return {
        id: dto.id,
        kind: dto.kind,
        installs,
        configuredMcpServers: dto.kind === SaasAiAgentKind.HARNESS ? configuredMcpServers : undefined,
        agentId: dto.extKey,
        displayName: dto.displayValue || dto.extKey,
        routineId,
        userId: dto.uid ?? undefined,
        ownerName: dto.userEmail?.split('@')[0]?.replace(/\./g, ' ') ?? 'Unknown',
        ownerEmail: dto.userEmail ?? '',
        ownerDepartment: '',
        vendor: vendorFromHarness(harnessType ?? harness),
        model: models[0] ?? '-',
        harness,
        discoveredAt: dto.discoveredAt?.slice(0, 10) ?? '',
        runFrequency: '-',
        lastRanAt: dto.lastSeenAt ?? dto.updatedAt ?? dto.discoveredAt ?? '',
        // Observed (tool calls) plus configured (endpoint harness) servers.
        mcpServers: [...new Set([...observedMcpServers, ...configuredNames])],
        mcpToolCalls: mcpToolCallsFromStatistics(mcpServiceCalls),
        findings: [],
        sessionCount,
        totalToolCalls,
        assistantTurns,
        userTurns,
        maxToolCallsBetweenUserMessages: maxChain,
        autonomyScore,
        mcpToolNames:
            mcpServiceCalls?.flatMap((svc) => svc.tool_calls.map((tc) => tc.tool.replace(/^mcp__/, ''))) ?? undefined,
        toolResultTurns: stats?.tool_result_turns ?? undefined,
        purpose: attestation?.user_attested_purpose ?? '',
        accessesSensitiveData: false,
        criticality: (attestation?.user_attested_criticality as AgentRecord['criticality']) ?? 'MEDIUM',
        runDuration: '-',
        authorization: '-',
        certifiedByOwner,
        certificationDate: attestation?.user_attestation_date?.slice(0, 10) ?? '',
    };
};

/**
 * Overlay reconciled platform fields onto a Contentful mock row when both share the same vendor agent id.
 */
export const mergeAgentRecords = (mock: AgentRecord, fromApi: AgentRecord): AgentRecord => ({
    ...mock,
    ...fromApi,
    id: fromApi.id,
    sessionCount: fromApi.sessionCount,
    findings: mock.findings.length > 0 ? mock.findings : fromApi.findings,
    purpose: mock.purpose || fromApi.purpose,
    ownerName: mock.ownerName || fromApi.ownerName,
    ownerDepartment: mock.ownerDepartment || fromApi.ownerDepartment,
    runFrequency: mock.runFrequency !== '-' ? mock.runFrequency : fromApi.runFrequency,
    runDuration: mock.runDuration !== '-' ? mock.runDuration : fromApi.runDuration,
    authorization: mock.authorization !== '-' ? mock.authorization : fromApi.authorization,
    certifiedByOwner: fromApi.certifiedByOwner || mock.certifiedByOwner,
    certificationDate: fromApi.certificationDate || mock.certificationDate,
});
